import React, { useEffect, useState, useMemo } from 'react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input, Textarea } from '@/components/ui/Input';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/Table';
import { Modal } from '@/components/ui/Modal';
import { Badge } from '@/components/ui/Badge';
import { api } from '@/api/client';
import { useAuth } from '@/context/AuthContext';
import { formatCurrency, useCurrency } from '@/hooks/useCurrency';
import { Plus, Edit, Trash, Building2, History, Search, Phone, Mail } from 'lucide-react';

export const Suppliers = () => {
  const currency = useCurrency();
  const { isSuperAdmin } = useAuth();
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', contact_person: '', phone: '', email: '', address: '', notes: '' });

  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.getSuppliers();
      setSuppliers(res.data);
    } finally { setLoading(false); }
  };

  const filtered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return suppliers;
    return suppliers.filter(s =>
      (s.name || '').toLowerCase().includes(q) ||
      (s.contact_person || '').toLowerCase().includes(q) ||
      (s.phone || '').toLowerCase().includes(q) ||
      (s.email || '').toLowerCase().includes(q)
    );
  }, [suppliers, searchTerm]);

  const openNew = () => {
    setEditing(null);
    setForm({ name: '', contact_person: '', phone: '', email: '', address: '', notes: '' });
    setShowForm(true);
  };

  const openEdit = (s) => {
    setEditing(s);
    setForm({
      name: s.name || '', contact_person: s.contact_person || '', phone: s.phone || '',
      email: s.email || '', address: s.address || '', notes: s.notes || ''
    });
    setShowForm(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { alert('Supplier name is required'); return; }
    try {
      if (editing) {
        await api.updateSupplier(editing.id, form);
      } else {
        await api.createSupplier(form);
      }
      setShowForm(false);
      await load();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to save supplier');
    }
  };

  const handleDelete = async (s) => {
    if (!window.confirm(`Delete supplier ${s.name}?`)) return;
    try {
      await api.deleteSupplier(s.id);
      await load();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to delete supplier');
    }
  };

  const showHistory = async (s) => {
    setHistoryOpen(true);
    setHistory(null);
    setLoadingHistory(true);
    try {
      const res = await api.getSupplierHistory(s.id);
      setHistory(res.data);
    } catch (err) {
      alert('Failed to load history');
      setHistoryOpen(false);
    } finally { setLoadingHistory(false); }
  };

  return (
    <div className="space-y-6" data-testid="suppliers-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Building2 size={28} className="text-blue-500" /> Suppliers
          </h1>
          <p className="text-sm text-[var(--text-muted)]">
            Master list — auto-populated from GRNs. Prevents duplicate spellings.
          </p>
        </div>
        <Button variant="primary" onClick={openNew} data-testid="new-supplier-btn">
          <Plus size={16} className="mr-1" /> New Supplier
        </Button>
      </div>

      <Card>
        <div className="mb-4 relative max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
          <input
            type="text"
            placeholder="Search suppliers…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-[var(--border)] bg-[var(--surface)] focus:outline-none focus:ring-2 focus:ring-blue-500"
            data-testid="supplier-search"
          />
        </div>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Code</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Phone</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Address</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={8} className="text-center py-8 text-gray-500">Loading…</TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={8} className="text-center py-8 text-gray-500">
                {suppliers.length === 0 ? 'No suppliers yet. Create one or add via a GRN.' : 'No suppliers match your search.'}
              </TableCell></TableRow>
            ) : (
              filtered.map(s => (
                <TableRow key={s.id} data-testid={`supplier-row-${s.id}`}>
                  <TableCell className="font-mono text-xs font-semibold text-blue-600 dark:text-blue-400" data-testid={`supplier-code-${s.id}`}>
                    {s.supplier_code || <span className="text-gray-400">—</span>}
                  </TableCell>
                  <TableCell className="font-medium">{s.name}</TableCell>
                  <TableCell>{s.contact_person || <span className="text-gray-400">—</span>}</TableCell>
                  <TableCell>{s.phone ? <span className="inline-flex items-center gap-1"><Phone size={12} />{s.phone}</span> : <span className="text-gray-400">—</span>}</TableCell>
                  <TableCell>{s.email ? <span className="inline-flex items-center gap-1"><Mail size={12} />{s.email}</span> : <span className="text-gray-400">—</span>}</TableCell>
                  <TableCell className="max-w-xs truncate">{s.address || <span className="text-gray-400">—</span>}</TableCell>
                  <TableCell><Badge variant={s.active ? 'success' : 'default'}>{s.active ? 'Active' : 'Inactive'}</Badge></TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => showHistory(s)} data-testid={`view-history-${s.id}`}>
                        <History size={14} />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => openEdit(s)} data-testid={`edit-supplier-${s.id}`}>
                        <Edit size={14} />
                      </Button>
                      {isSuperAdmin && (
                        <Button size="sm" variant="ghost" onClick={() => handleDelete(s)} data-testid={`delete-supplier-${s.id}`}>
                          <Trash size={14} className="text-red-500" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Create/Edit Modal */}
      <Modal isOpen={showForm} onClose={() => setShowForm(false)} title={editing ? 'Edit Supplier' : 'New Supplier'} size="md">
        <form onSubmit={handleSave} className="space-y-3">
          <Input label="Name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="supplier-form-name" />
          <div className="grid grid-cols-2 gap-3">
            <Input label="Contact Person" value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} data-testid="supplier-form-contact" />
            <Input label="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="supplier-form-phone" />
          </div>
          <Input label="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="supplier-form-email" />
          <Input label="Address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="supplier-form-address" />
          <Textarea label="Notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="supplier-form-notes" />
          <div className="flex justify-end gap-2 pt-2 border-t border-[var(--border)]">
            <Button type="button" variant="secondary" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button type="submit" data-testid="supplier-form-save">{editing ? 'Update' : 'Create'}</Button>
          </div>
        </form>
      </Modal>

      {/* History Modal */}
      <Modal isOpen={historyOpen} onClose={() => setHistoryOpen(false)} title="Supplier History" size="xl">
        {loadingHistory && <p className="text-center py-8">Loading…</p>}
        {history && (
          <div className="space-y-4" data-testid="supplier-history-modal">
            <div>
              <h3 className="text-lg font-semibold flex items-center gap-2">
                <Building2 size={18} className="text-blue-500" /> {history.supplier.name}
                {history.supplier.supplier_code && (
                  <span className="text-xs font-mono font-normal text-blue-600 dark:text-blue-400 ml-1" data-testid="history-supplier-code">
                    ({history.supplier.supplier_code})
                  </span>
                )}
              </h3>
              <div className="text-xs text-[var(--text-muted)] mt-1 flex flex-wrap gap-3">
                {history.supplier.contact_person && <span>Contact: {history.supplier.contact_person}</span>}
                {history.supplier.phone && <span>Phone: {history.supplier.phone}</span>}
                {history.supplier.email && <span>Email: {history.supplier.email}</span>}
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="supplier-stats">
              <StatCard label="Total GRNs" value={history.stats.total_grns} />
              <StatCard label="Total Units" value={history.stats.total_units} />
              <StatCard label="Total Purchases" value={formatCurrency(history.stats.total_purchases, currency)} highlight />
              <StatCard label="Outstanding" value={formatCurrency(history.stats.outstanding, currency)} negative={history.stats.outstanding > 0} />
              <StatCard label="Last Purchase" value={history.stats.last_purchase_date || '—'} />
            </div>

            <div>
              <h4 className="text-sm font-semibold mb-2">Purchase History</h4>
              {history.grns.length === 0 ? (
                <p className="text-sm text-[var(--text-muted)] text-center py-4">No GRNs recorded yet for this supplier.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Invoice #</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Items</TableHead>
                      <TableHead>Units</TableHead>
                      <TableHead>Subtotal</TableHead>
                      <TableHead>Payment</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {history.grns.map(g => (
                      <TableRow key={g.id} data-testid={`history-grn-${g.id}`}>
                        <TableCell className="font-mono text-xs">{g.invoice_number}</TableCell>
                        <TableCell>{g.received_date}</TableCell>
                        <TableCell>{g.item_count}</TableCell>
                        <TableCell>{g.units}</TableCell>
                        <TableCell className="font-semibold">{formatCurrency(g.subtotal, currency)}</TableCell>
                        <TableCell>
                          <Badge variant={
                            g.payment_status === 'Paid' ? 'success' :
                            g.payment_status === 'Credit' ? 'warning' : 'default'
                          }>{g.payment_status || 'Unknown'}</Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

const StatCard = ({ label, value, highlight, negative }) => (
  <div className={`rounded-lg border p-3 ${highlight ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20' : negative ? 'border-red-500 bg-red-50 dark:bg-red-900/20' : 'border-[var(--border)] bg-[var(--surface)]'}`}>
    <div className="text-[10px] uppercase tracking-wide text-[var(--text-muted)]">{label}</div>
    <div className={`text-lg font-bold ${negative ? 'text-red-600 dark:text-red-400' : ''}`}>{value}</div>
  </div>
);

export default Suppliers;
